import { ArrowRight, CreditCard, ShieldCheck } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';

/**
 * Update / add card sheet. Clicking the primary CTA fires `onConfirm` which
 * creates a Stripe Checkout `setup` session via
 * `POST /api/billing/checkout-session/{shopId}` and redirects (handled by the
 * caller). Used for both "update card" and "add a card to reactivate" —
 * `mode` toggles copy.
 */
export function UpdateCardSheet({ open, onOpenChange, mode = 'update', onConfirm, pending = false }) {
	const adding = mode === 'add';
	const heading = adding ? 'Add a payment method' : 'Update your card';
	const sub = adding
		? "We'll take you to Stripe to add your card. Once it's saved, your subscription resumes automatically and you come straight back here."
		: "We'll take you to Stripe to update your card. Your next charge will use the new one — and you come straight back here.";

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent className="flex flex-col gap-6">
				<SheetHeader>
					<div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-action-soft text-action-soft-foreground">
						<CreditCard className="h-5 w-5" />
					</div>
					<SheetTitle>{heading}</SheetTitle>
					<SheetDescription>{sub}</SheetDescription>
				</SheetHeader>

				<ul className="space-y-3 text-sm text-foreground">
					<li className="flex items-start gap-3">
						<ShieldCheck className="mt-0.5 h-4 w-4 text-success" />
						<span>Card details go straight to Stripe — we never see them.</span>
					</li>
					<li className="flex items-start gap-3">
						<ShieldCheck className="mt-0.5 h-4 w-4 text-success" />
						<span>Cancel any time before the trial ends and you won't be charged.</span>
					</li>
				</ul>

				<SheetFooter>
					<Button variant="ghost" onClick={() => onOpenChange?.(false)} disabled={pending}>
						Not now
					</Button>
					<Button variant="action" onClick={onConfirm} disabled={pending}>
						{pending ? 'Opening Stripe…' : 'Continue to Stripe'}
						{pending ? null : <ArrowRight className="h-4 w-4" />}
					</Button>
				</SheetFooter>
			</SheetContent>
		</Sheet>
	);
}
